// Pasted HTML that carries pictures - part of a web page, or content copied out of another editor - reaches the
// editor through draft-js, which turns each <img> into a camera emoji in the line of text, carrying the picture.
// The editor only draws a picture that is a line of its own, so the emoji is all anyone saw, and saving drops any
// line holding a picture - the words beside it included (toMarkdown's filter, there since 2023). Each picture is
// lifted onto a line of its own as the paste lands, where it is drawn, sized and saved like any other.

import { BlockMapBuilder, CharacterMetadata, ContentBlock, ContentState, EditorState, Modifier, SelectionState, convertFromHTML, genKey } from 'draft-js';
import EditorBidiService from 'draft-js/lib/EditorBidiService';
import { EDITOR_ONLY_LINE } from './blankLines';

const isPicture = (content: ContentState, character: CharacterMetadata): boolean => {
  const key = character.getEntity();
  return !!key && content.getEntity(key).getType() === 'IMAGE';
};

const isSpace = (ch: string | undefined) => ch !== undefined && /\s/.test(ch);

/**
 * The content with every picture that sits inside a line of text moved onto a line of its own, the text before and
 * after it kept on lines either side - or null when there is none, which is every change but a paste. The cursor
 * goes after the last piece of the line it was in. `keep` decides, from a picture's address, whether it can be saved
 * at all; one that cannot is left out rather than lifted, so nothing is drawn that will not be kept.
 */
export const liftInlinePictures = (
  content: ContentState, selection: SelectionState, keep: (src: string) => boolean = () => true
): { content: ContentState; selection: SelectionState } | null => {
  const blocks: ContentBlock[] = content.getBlockMap().toArray();
  if (!blocks.some(block => block.getType() !== 'atomic' && block.getCharacterList().some((ch: CharacterMetadata) => isPicture(content, ch)))) return null;

  const out: ContentBlock[] = [];
  const lifted = new Set<string>();
  let cursorBlock: ContentBlock | null = null;

  blocks.forEach(block => {
    const ranges: [number, number][] = [];
    if (block.getType() !== 'atomic') block.findEntityRanges((ch: CharacterMetadata) => isPicture(content, ch), (start: number, end: number) => ranges.push([start, end]));
    if (ranges.length === 0) {
      out.push(block);
      return;
    }

    const text: string = block.getText();
    const pieces: ContentBlock[] = [];
    const nextKey = () => (pieces.length === 0 ? block.getKey() : genKey());
    const pushText = (from: number, to: number) => {
      while (from < to && isSpace(text[from])) from++;
      while (to > from && isSpace(text[to - 1])) to--;
      if (from >= to) return;
      pieces.push(new ContentBlock({
        key: nextKey(), type: block.getType(), depth: block.getDepth(), data: block.getData(),
        text: text.slice(from, to), characterList: block.getCharacterList().slice(from, to).toList()
      }));
    };

    let at = 0;
    ranges.forEach(([start, end]) => {
      pushText(at, start);
      const entity = block.getEntityAt(start);
      if (keep(content.getEntity(entity).getData()?.src ?? '')) {
        const picture = new ContentBlock({
          key: nextKey(), type: 'atomic', text: ' ',
          characterList: block.getCharacterList().clear().push(CharacterMetadata.create({ entity })),
          data: block.getData().clear()
        });
        lifted.add(picture.getKey());
        pieces.push(picture);
      }
      at = end;
    });
    pushText(at, text.length);

    // A line that held nothing but a picture that cannot be kept still needs to exist if it held the cursor.
    if (pieces.length === 0 && block.getKey() === selection.getFocusKey()) pieces.push(new ContentBlock({ key: block.getKey(), type: 'unstyled', text: '', characterList: block.getCharacterList().clear(), data: block.getData().clear() }));
    out.push(...pieces);
    if (block.getKey() === selection.getFocusKey()) cursorBlock = pieces[pieces.length - 1];
  });

  // As on load: a picture needs a line after it only where there is nothing else to put the cursor on - when it is
  // last, or another picture follows. Marked, so saving drops it while it is still empty.
  const withLines: ContentBlock[] = [];
  out.forEach((block, i) => {
    withLines.push(block);
    const next = out[i + 1];
    if (!lifted.has(block.getKey()) || (next && next.getType() !== 'atomic')) return;
    const line = new ContentBlock({
      key: genKey(), type: 'unstyled', text: '', characterList: block.getCharacterList().clear(),
      data: block.getData().clear().set(EDITOR_ONLY_LINE, true)
    });
    withLines.push(line);
    if (cursorBlock === block) cursorBlock = line;
  });
  if (cursorBlock?.getType() === 'atomic') cursorBlock = withLines[withLines.indexOf(cursorBlock) + 1] ?? cursorBlock;

  const cursor = cursorBlock
    ? SelectionState.createEmpty(cursorBlock.getKey()).merge({ anchorOffset: cursorBlock.getLength(), focusOffset: cursorBlock.getLength(), hasFocus: selection.getHasFocus() }) as SelectionState
    : selection;
  return {
    content: content.merge({ blockMap: BlockMapBuilder.createFromArray(withLines), selectionBefore: selection, selectionAfter: cursor }) as ContentState,
    selection: cursor
  };
};

/**
 * A pasted <figure><img> - which is how a picture copied out of another catalog item's editor arrives - comes from
 * draft-js as a picture line of its own, with the camera emoji as its text. Two things went wrong with that. It
 * skipped `liftInlinePictures`, so nobody asked whether it could be kept: it was drawn from the other editor's
 * private `blob:` address, never uploaded, and saved as the emoji. And when the copy ended on a picture, the cursor
 * landed inside that line, and react-draft-wysiwyg throws away any change that leaves it there - so the whole
 * paste vanished. Turned back into a picture inside a line, it goes the same way as one from a web page.
 */
export const picturesAsInline = (blocks: ContentBlock[], content: ContentState): ContentBlock[] =>
  blocks.map(block => block.getType() === 'atomic' && block.getCharacterList().some((ch: CharacterMetadata) => isPicture(content, ch))
    ? block.merge({ type: 'unstyled', data: block.getData().clear() }) as ContentBlock
    : block);

// Private use, so it can never be real text. Held in an empty line through draft-js's conversion and cleared after.
const EMPTY_LINE = '';

/** True for HTML copied out of a draft-js editor, which marks every line it renders - another catalog item's, say. */
export const isDraftEditorHtml = (html: string) => /\bdata-block="true"/.test(html ?? '');

/**
 * Draft-js's HTML conversion drops a line with nothing in it, so the empty lines of a copied catalog answer
 * vanished on the way in. A copy from a draft-js editor marks each line, so each empty one is filled with a
 * placeholder the conversion keeps, and cleared once it is a line. Empty lines at the end are not kept: there they
 * are the editor's own line after a picture, which lifting adds back where it is needed, or nothing at all.
 * HTML from anywhere else carries no such marks and is converted exactly as before.
 */
const holdEmptyLines = (html: string): string => {
  if (!isDraftEditorHtml(html) || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lines = Array.from(doc.body.querySelectorAll('[data-block="true"]')).filter(el => !el.parentElement?.closest('[data-block="true"]'));
  const isEmpty = (el: Element) => !el.textContent?.trim() && !el.querySelector('img');
  while (lines.length > 0 && isEmpty(lines[lines.length - 1])) lines.pop().remove();
  lines.filter(isEmpty).forEach(el => {
    const breaks = el.querySelectorAll('br');
    if (breaks.length > 0) breaks.forEach(br => br.replaceWith(doc.createTextNode(EMPTY_LINE)));
    else el.appendChild(doc.createTextNode(EMPTY_LINE));
  });
  return doc.body.innerHTML;
};

const clearHeldLines = (blocks: ContentBlock[]): ContentBlock[] =>
  blocks.map(block => block.getText() === EMPTY_LINE
    ? block.merge({ text: '', characterList: block.getCharacterList().clear() }) as ContentBlock
    : block);

/** A stylesheet in pasted HTML - Word's always carries one. */
export const hasPastedStyles = (html: string) => /<style\b/i.test(html ?? '');

/**
 * Word's copy carries its stylesheet - a `<style>` opening with its font definitions. Draft-js drops one in
 * the document's head, but has no rule for a `<style>` among the content and turns everything in it into text, so
 * a paste from Word (measured through the Mac's clipboard into the VM) began with 1,700 characters of CSS. Taken out
 * before converting, with scripts and comments, none of which is ever content.
 */
export const withoutPastedMarkup = (html: string): string =>
  (html ?? '')
    .replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '');

/**
 * Pasted HTML as draft-js would insert it, except that its pictures arrive inside lines - see `picturesAsInline` -
 * a copy from another catalog item keeps its empty lines - see `holdEmptyLines` - and a stylesheet never becomes
 * text - see `withoutPastedMarkup`. Null when there is nothing to insert.
 */
export const pasteHtmlWithPictures = (editorState: EditorState, html: string, blockRenderMap?: unknown): EditorState | null => {
  const parsed: { contentBlocks: ContentBlock[]; entityMap: unknown } | null = convertFromHTML(holdEmptyLines(withoutPastedMarkup(html)), undefined, blockRenderMap);
  if (!parsed?.contentBlocks?.length) return null;
  const content = editorState.getCurrentContent();
  const fragment = BlockMapBuilder.createFromArray(clearHeldLines(picturesAsInline(parsed.contentBlocks, content)));
  const pasted = Modifier.replaceWithFragment(content, editorState.getSelection(), fragment);
  return EditorState.push(editorState, pasted.set('entityMap', parsed.entityMap) as ContentState, 'insert-fragment');
};

/**
 * The editor state with its pictures lifted (see `liftInlinePictures`), or as it was when there is nothing to lift.
 * Set rather than pushed, so it is not an undo step of its own - and EditorState.set, unlike push, does not work out
 * the text direction of the lines lifting creates. Draft-js crashes drawing a link in a line with no direction, so a
 * web page's line holding both a picture and a link took the whole page down until it was worked out here.
 */
export const withPicturesLifted = (editorState: EditorState, keep: (src: string) => boolean): EditorState => {
  const lifted = liftInlinePictures(editorState.getCurrentContent(), editorState.getSelection(), keep);
  if (!lifted) return editorState;
  const directionMap = EditorBidiService.getDirectionMap(lifted.content, editorState.getDirectionMap());
  return EditorState.forceSelection(EditorState.set(editorState, { currentContent: lifted.content, directionMap }), lifted.selection);
};
